import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { compensationsShape, paymentsShape } from 'constants/propTypes';
import { isEmpty } from 'ramda';
import { useForm } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';

import CardSelect from 'components/ui/CardSelect';
import Button from 'components/ui/Button';
import { Row } from 'components/shared/Layout';
import FormattedCurrency from 'components/ui/FormattedCurrency';
import { REFUND_100, REFUND_25, REFUND_50 } from 'constants/prrRefund';
import { codes as paymentCodes } from 'constants/payment';

import { AmountLabel, ErrorMessage } from '../../components/ui/Payments/SplitRefundMethods/CardRefund/CardRefund.styles';

import { getTotalPrice, getRefundAmount, getDisplayForPayment, getRefundOptions } from './PrrRefund.utils';
import RefundAmountSelection from './RefundAmountSelection';
import { StyledP } from './PrrRefund.styles';
import CompensationBreakdown from './CompensationBreakdown/CompensationBreakdown';

const PrrRefundCard = ({
  onSubmit,
  reference,
  filteredProducts,
  compensateLegs,
  compensationItems = [],
  payments,
  loading,
  itemRefs,
  currency
}) => {
  const serviceTypes = compensateLegs.map((leg) => leg.serviceType);

  const refundOptions = getRefundOptions(compensationItems, serviceTypes, [REFUND_25, REFUND_50]);

  const {
    register,
    handleSubmit,
    formState: { isValid },
    watch
  } = useForm({
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: {
      refundPercentage: String(refundOptions?.[0]?.value ?? REFUND_25.value)
    }
  });

  const { refundPercentage, pspReference } = watch();

  const { totalPrice, refundAmount } = useMemo(() => {
    let totalPrice = 0;
    let refundAmount = 0;

    if (compensationItems.length > 0) {
      compensationItems.forEach(({ ticketPrice, cashCompensationAmount }) => {
        const amount = refundPercentage === String(REFUND_100.value) ? ticketPrice : cashCompensationAmount;

        totalPrice += ticketPrice;
        refundAmount += amount;
      });
    } else {
      totalPrice = getTotalPrice(filteredProducts);
      refundAmount = getRefundAmount(totalPrice, refundPercentage);
    }

    return {
      totalPrice,
      refundAmount
    };
  }, [compensationItems, filteredProducts, refundPercentage]);

  const [maxRefundableAmount, setMaxRefundableAmount] = useState(totalPrice);

  const onSubmitForm = () => {
    const variables = {
      currency: compensationItems?.[0]?.currency ?? currency,
      pspReference,
      bookingReference: reference,
      refundAmount: Number(refundAmount),
      additionalDetails: itemRefs.map((ref) => {
        const product = filteredProducts.find(({ itemRef }) => itemRef === ref);
        const compensationRecord = compensationItems.find(({ ticketNumber }) => product?.ticketNumber === ticketNumber);

        let value = 0;

        if (compensationRecord) {
          if (refundPercentage === String(REFUND_100.value)) {
            value = compensationRecord.ticketPrice;
          } else {
            value = compensationRecord.cashCompensationAmount;
          }
        } else {
          value = getRefundAmount(product?.price ?? 0, refundPercentage);
        }

        return {
          itemRefId: Number(ref),
          key: `PRR_Compensation_${refundPercentage}%`,
          value: String(value)
        };
      }),
      ...(compensationItems.length > 0 && {
        compensationRecordsToUpdate: compensationItems.map(({ compensationRecordId, caseId }) => ({
          compensationRecordId,
          caseId
        }))
      })
    };

    onSubmit({ method: paymentCodes.CARD, variables });
  };

  return (
    <div>
      <h2>
        <FormattedMessage id="prrRefund.compensationAmount" />
      </h2>

      <Row>
        <form onSubmit={handleSubmit(onSubmitForm)}>
          <Row>
            <RefundAmountSelection options={refundOptions} register={register} />
          </Row>

          <h2>
            <FormattedMessage id="prrRefund.compensationTickets" />
          </h2>
          <CompensationBreakdown
            currency={currency}
            compensateLegs={compensateLegs}
            refundPercentage={refundPercentage}
            compensationItems={compensationItems}
          />

          <Row>
            <h2>
              <FormattedMessage id="prrRefund.processRefund" />
            </h2>
            <StyledP>
              <FormattedMessage id="prrRefund.refundRules" />
            </StyledP>
          </Row>

          <Row>
            <h3>
              <FormattedMessage id="prrRefund.selectCard" />
            </h3>
            <Row data-cy="credit-card-list" span={6}>
              {payments.map((p) => {
                const availableAmount = p.availableRefundAmount || 0;

                return (
                  <CardSelect
                    key={p.id}
                    id={p.id}
                    testId={p.reference}
                    value={p.reference}
                    onClick={() => setMaxRefundableAmount(availableAmount)}
                    {...register('pspReference', {
                      required: true,
                      disabled: currency !== p.currency || availableAmount <= 0
                    })}
                  >
                    {getDisplayForPayment(p)}
                    <div>
                      <AmountLabel>
                        <FormattedMessage id="refunds.original" />
                      </AmountLabel>
                      <FormattedCurrency dataCy={`prr-original-amount-${p.reference}-input`} currency={p.currency} amount={p.amount} />
                    </div>
                    <div>
                      <AmountLabel>
                        <FormattedMessage id="refunds.available" />
                      </AmountLabel>
                      <FormattedCurrency
                        dataCy={`prr-available-amount-${p.reference}-input`}
                        currency={p.currency}
                        amount={availableAmount}
                      />
                    </div>
                  </CardSelect>
                );
              })}
            </Row>
          </Row>

          {maxRefundableAmount < refundAmount && (
            <ErrorMessage data-cy="card-max-amount-error-msg">
              <FormattedMessage id="refunds.card.maxAmountError" />
              <FormattedCurrency currency={currency} amount={maxRefundableAmount} />
            </ErrorMessage>
          )}

          <Button
            data-cy="submit-prr-refund"
            type="submit"
            disabled={!isValid || !pspReference || loading || isEmpty(payments) || maxRefundableAmount < refundAmount}
            loading={loading}
          >
            <FormattedMessage id="prrRefund.processRefundCta" />
          </Button>
        </form>
      </Row>
    </div>
  );
};

PrrRefundCard.propTypes = {
  filteredProducts: PropTypes.arrayOf(
    PropTypes.shape({
      itemRef: PropTypes.string,
      passengerType: PropTypes.string,
      bucketCode: PropTypes.string,
      productFamily: PropTypes.shape({
        code: PropTypes.string
      }),
      currency: PropTypes.string,
      loyaltyPoints: PropTypes.number,
      price: PropTypes.number
    })
  ),
  reference: PropTypes.string,
  onSubmit: PropTypes.func,
  payments: paymentsShape.isRequired,
  loading: PropTypes.bool,
  itemRefs: PropTypes.arrayOf(PropTypes.string),
  compensateLegs: PropTypes.arrayOf(
    PropTypes.shape({
      legName: PropTypes.string,
      products: PropTypes.arrayOf(
        PropTypes.shape({
          itemRef: PropTypes.string,
          passengerType: PropTypes.string,
          bucketCode: PropTypes.string,
          productFamily: PropTypes.shape({
            code: PropTypes.string
          }),
          currency: PropTypes.string,
          loyaltyPoints: PropTypes.number,
          price: PropTypes.number
        })
      )
    })
  ),
  compensationItems: compensationsShape,
  currency: PropTypes.string
};

export default PrrRefundCard;
